import { useEffect, useRef, useState } from "react";
import { uploadFiles } from "@/lib/api";
import { Upload, X, Check } from "lucide-react";

export const Field = ({ label, hint, required, children, testId }) => (
  <label className="block" data-testid={testId}>
    <span className="block text-sm font-medium text-pg-dark">
      {label} {required && <span className="text-pg-primary">*</span>}
    </span>
    {hint && <span className="mt-1 block text-[13px] leading-snug text-pg-muted sm:text-xs">{hint}</span>}
    <div className="mt-2">{children}</div>
  </label>
);

// 16px on a phone: anything smaller makes iOS zoom the page in when a field is tapped.
const baseInput =
  "w-full rounded-md border border-pg-border bg-white px-4 py-3 text-base text-pg-text outline-none transition-colors focus:border-pg-primary sm:text-sm";

export const TextInput = (props) => <input {...props} className={`${baseInput} ${props.className || ""}`} />;

// Grows with what is typed so nothing you write is ever hidden behind a scrollbar.
export const TextArea = ({ rows = 4, className = "", ...props }) => {
  const ref = useRef(null);
  const resize = () => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };
  useEffect(resize, [props.value]);
  useEffect(() => {
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);
  return (
    <textarea
      ref={ref}
      rows={rows}
      {...props}
      onInput={resize}
      className={`${baseInput} resize-y overflow-hidden ${className}`}
    />
  );
};

export const SelectInput = ({ options, placeholder = "Please select", ...props }) => (
  <select {...props} className={`${baseInput} ${props.className || ""}`}>
    {placeholder ? <option value="">{placeholder}</option> : null}
    {options.map((option) => {
      const value = typeof option === "string" ? option : option.value;
      const label = typeof option === "string" ? option : option.label;
      return (
        <option key={value} value={value}>
          {label}
        </option>
      );
    })}
  </select>
);

export const OptionGrid = ({ options, value, onChange, multiple = false, columns = 2, testId }) => {
  const selected = multiple ? value || [] : value;
  const toggle = (option) => {
    if (!multiple) return onChange(option);
    return onChange(selected.includes(option) ? selected.filter((v) => v !== option) : [...selected, option]);
  };
  const isOn = (option) => (multiple ? selected.includes(option) : selected === option);
  const columnClass = columns === 3 ? "sm:grid-cols-3" : columns === 1 ? "" : "sm:grid-cols-2";
  return (
    <div className={`grid gap-3 ${columnClass}`} data-testid={testId}>
      {options.map((option) => (
        <button
          key={option}
          type="button"
          onClick={() => toggle(option)}
          data-testid={`option-${option.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          className={`flex !min-h-[52px] items-center justify-between gap-2 rounded-md border px-4 py-3 text-left text-[15px] transition-colors sm:!min-h-0 sm:text-sm ${
            isOn(option)
              ? "border-pg-primary bg-pg-pale font-semibold text-pg-dark"
              : "border-pg-border bg-white text-pg-text hover:border-pg-primary"
          }`}
        >
          <span>{option}</span>
          {isOn(option) && <Check className="h-4 w-4 shrink-0 text-pg-primary" />}
        </button>
      ))}
    </div>
  );
};

export const ProgressBar = ({ step, total, labels = [] }) => (
  <div data-testid="wizard-progress">
    <div className="flex items-center justify-between text-xs text-pg-muted">
      <span className="font-semibold text-pg-dark">
        Step {step} of {total}
      </span>
      {labels[step - 1] && <span>{labels[step - 1]}</span>}
    </div>
    <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-pg-pale">
      <div
        className="h-full rounded-full bg-pg-primary transition-all duration-500"
        style={{ width: `${(step / total) * 100}%` }}
      />
    </div>
  </div>
);

export const WizardNav = ({ onBack, onNext, nextLabel = "Continue", disabled, loading, showBack = true }) => (
  <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
    {showBack ? (
      <button
        type="button"
        onClick={onBack}
        data-testid="wizard-back"
        className="w-full rounded-md border border-pg-border px-5 py-3.5 text-sm font-semibold text-pg-dark transition-colors hover:border-pg-primary sm:w-auto sm:py-3"
      >
        Back
      </button>
    ) : (
      <span />
    )}
    <button
      type="button"
      onClick={onNext}
      disabled={disabled || loading}
      data-testid="wizard-next"
      className="w-full rounded-md bg-pg-primary px-6 py-3.5 text-base font-semibold text-white transition-colors hover:bg-pg-dark disabled:opacity-50 sm:w-auto sm:py-3 sm:text-sm"
    >
      {loading ? "Please wait…" : nextLabel}
    </button>
  </div>
);

export const UploadField = ({
  label,
  hint,
  kind = "photo",
  category = "",
  files,
  onChange,
  testId = "upload-field",
}) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const handle = async (event) => {
    const list = event.target.files;
    if (!list?.length) return;
    setBusy(true);
    setError("");
    try {
      const uploaded = await uploadFiles(list, kind, category);
      onChange([...(files || []), ...uploaded]);
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload failed. Please try a smaller file.");
    } finally {
      setBusy(false);
      event.target.value = "";
    }
  };

  return (
    <div data-testid={testId}>
      <p className="text-sm font-medium text-pg-dark">{label}</p>
      {hint && <p className="mt-1 text-xs leading-relaxed text-pg-muted">{hint}</p>}
      <label className="mt-3 flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-pg-border bg-pg-pale px-4 py-6 text-sm text-pg-muted hover:border-pg-primary transition-colors">
        <Upload className="h-4 w-4 text-pg-primary" />
        {busy ? "Uploading…" : kind === "photo" ? "Select photographs (JPG, PNG, WEBP up to 15MB)" : "Select documents (PDF, DOCX or images up to 15MB)"}
        <input
          type="file"
          multiple
          accept={kind === "photo" ? "image/*" : ".pdf,.doc,.docx,image/*"}
          onChange={handle}
          className="hidden"
          data-testid={`${testId}-input`}
        />
      </label>
      {error && <p className="mt-2 text-xs text-[#7A2020]">{error}</p>}
      {files?.length > 0 && (
        <ul className="mt-3 space-y-2" data-testid={`${testId}-list`}>
          {files.map((file) => (
            <li
              key={file.id}
              className="flex items-center justify-between gap-3 rounded-md border border-pg-border bg-white px-3 py-2 text-xs text-pg-text"
            >
              <span className="truncate">{file.filename}</span>
              <button
                type="button"
                onClick={() => onChange(files.filter((f) => f.id !== file.id))}
                className="text-pg-muted hover:text-pg-primary"
                aria-label={`Remove ${file.filename}`}
              >
                <X className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const LeadFields = ({ values, set, showPostcode = true }) => (
  <div className="grid gap-5 sm:grid-cols-2">
    <Field label="Full name" required testId="lead-name">
      <TextInput
        value={values.name}
        onChange={(e) => set("name", e.target.value)}
        data-testid="lead-name-input"
        autoComplete="name"
      />
    </Field>
    <Field label="Email address" required testId="lead-email">
      <TextInput
        type="email"
        value={values.email}
        onChange={(e) => set("email", e.target.value)}
        data-testid="lead-email-input"
        autoComplete="email"
      />
    </Field>
    <Field label="Telephone number" required testId="lead-phone">
      <TextInput
        value={values.phone}
        onChange={(e) => set("phone", e.target.value)}
        data-testid="lead-phone-input"
        autoComplete="tel"
      />
    </Field>
    {showPostcode && (
      <Field label="Property postcode" required testId="lead-postcode">
        <TextInput
          value={values.postcode}
          onChange={(e) => set("postcode", e.target.value)}
          data-testid="lead-postcode-input"
          autoComplete="postal-code"
        />
      </Field>
    )}
    <Field label="Preferred contact method" testId="lead-preferred-contact">
      <SelectInput
        options={["Email", "Telephone", "WhatsApp", "Text message"]}
        value={values.preferred_contact}
        onChange={(e) => set("preferred_contact", e.target.value)}
        placeholder="Email"
        data-testid="lead-preferred-contact-input"
      />
    </Field>
  </div>
);
